import { useCallback, useMemo, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Dialog } from "@/components/ui/Dialog";
import { TextareaInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import { useRealtimeSync } from "@/lib/useRealtime";
import {
  ABSENCE_STATUS_LABELS,
  deleteAbsenceRequest,
  formatAbsenceDateRange,
  formatAbsenceDays,
  getAbsenceTypeLabel,
  listAbsenceRequests,
  type AbsenceStatus,
  type EmployeeAbsenceRequest,
} from "@/lib/employeeAbsences";
import { useRefreshKeyLoad } from "@/pages/admin/zeiterfassung/useRefreshKeyLoad";

interface AbsenceOfficePanelProps {
  refreshKey: number;
  onChanged: () => void;
}

const STATUS_FILTER_OPTIONS: Array<{ value: AbsenceStatus | ""; label: string }> = [
  { value: "", label: "Alle Status" },
  { value: "pending", label: ABSENCE_STATUS_LABELS.pending },
  { value: "approved", label: ABSENCE_STATUS_LABELS.approved },
  { value: "rejected", label: ABSENCE_STATUS_LABELS.rejected },
  { value: "cancelled", label: ABSENCE_STATUS_LABELS.cancelled },
];

function statusBadgeClass(status: AbsenceStatus): string {
  switch (status) {
    case "approved":
      return "bg-status-green/15 text-status-green";
    case "pending":
      return "bg-status-orange/15 text-status-orange";
    case "rejected":
      return "bg-status-red/15 text-status-red";
    default:
      return "bg-fill-tertiary text-label-secondary";
  }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function AbsenceOfficePanel({ refreshKey, onChanged }: AbsenceOfficePanelProps) {
  const currentYear = new Date().getFullYear();
  const [items, setItems] = useState<EmployeeAbsenceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [year, setYear] = useState<number | "">(currentYear);
  const [status, setStatus] = useState<AbsenceStatus | "">("");
  const [search, setSearch] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<EmployeeAbsenceRequest | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!options?.silent) setLoading(true);
      setError(null);
      try {
        const list = await listAbsenceRequests({
          year: typeof year === "number" ? year : undefined,
          status: status || undefined,
        });
        setItems(list);
      } catch (err) {
        setError(
          err instanceof ApiError ? err.message : "Abwesenheitsanträge konnten nicht geladen werden.",
        );
        if (!options?.silent) setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [year, status],
  );

  useRefreshKeyLoad(load, refreshKey);

  useRealtimeSync({
    domains: ["employee-times"],
    paused: deleteTarget != null,
    onChange: () => void load({ silent: true }),
  });

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) => {
      const blob = [it.user_display, it.user_login, getAbsenceTypeLabel(it.absence_type), it.note]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(needle);
    });
  }, [items, search]);

  function openDelete(item: EmployeeAbsenceRequest) {
    setDeleteTarget(item);
    setDeleteReason("");
  }

  function closeDelete() {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteReason("");
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    const reason = deleteReason.trim();
    if (reason.length < 3) {
      toast.error("Bitte eine Begründung mit mindestens 3 Zeichen angeben.");
      return;
    }
    setDeleting(true);
    try {
      await deleteAbsenceRequest(deleteTarget.id, reason);
      toast.success("Abwesenheit wurde gelöscht (Begründung gespeichert).");
      setItems((prev) =>
        prev.map((it) =>
          it.id === deleteTarget.id
            ? {
                ...it,
                status: "cancelled" as const,
                review_comment: `Gelöscht durch Büro: ${reason}`,
              }
            : it,
        ),
      );
      setDeleteTarget(null);
      setDeleteReason("");
      onChanged();
      await load({ silent: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
        <h2 className="text-lg font-semibold text-label">Abwesenheitsanträge (Büro)</h2>
        <p className="mt-1 text-sm text-label-secondary">
          Alle App-Abwesenheitsanträge im Überblick. Genehmigte Einträge können mit
          Pflicht-Begründung gelöscht werden – sie verschwinden dann aus Montageplan und
          Jahresübersicht. Hero-Abwesenheiten und automatische Brückentage stehen hier nicht.
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="eh-label text-xs">Jahr</span>
            <input
              type="number"
              className="eh-input w-28"
              value={year}
              onChange={(e) => {
                const v = e.target.value.trim();
                setYear(v === "" ? "" : Number(v));
              }}
            />
          </label>
          <label className="space-y-1">
            <span className="eh-label text-xs">Status</span>
            <select
              className="eh-input w-44"
              value={status}
              onChange={(e) => setStatus(e.target.value as AbsenceStatus | "")}
            >
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="min-w-[12rem] flex-1 space-y-1">
            <span className="eh-label text-xs">Suche</span>
            <input
              type="search"
              className="eh-input w-full"
              placeholder="Name, Typ, Notiz …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
        </div>
      </div>

      {error ? (
        <Alert variant="error" title="Fehler">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : filtered.length === 0 ? (
        <Alert variant="info" title="Keine Anträge">
          {items.length === 0
            ? "Für die gewählten Filter sind keine Abwesenheitsanträge vorhanden."
            : "Keine Treffer für die Suche."}
        </Alert>
      ) : (
        <div className="overflow-x-auto rounded-ios border border-separator bg-card shadow-ios">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-separator bg-fill-quaternary/40 text-left text-xs uppercase tracking-wide text-label-tertiary">
                <th className="px-4 py-2 font-medium">Mitarbeiter</th>
                <th className="px-4 py-2 font-medium">Typ</th>
                <th className="px-4 py-2 font-medium">Zeitraum</th>
                <th className="px-4 py-2 font-medium">Tage</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Beantragt</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id} className="border-b border-separator/60 last:border-b-0">
                  <td className="px-4 py-3">
                    <div className="font-medium text-label">{item.user_display || item.user_login}</div>
                    {item.note ? (
                      <div className="mt-0.5 max-w-xs truncate text-xs text-label-tertiary" title={item.note}>
                        {item.note}
                      </div>
                    ) : null}
                    {item.status === "cancelled" && item.review_comment ? (
                      <div
                        className="mt-0.5 max-w-xs truncate text-xs text-label-tertiary"
                        title={item.review_comment}
                      >
                        {item.review_comment}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-label-secondary">{getAbsenceTypeLabel(item.absence_type)}</td>
                  <td className="px-4 py-3 text-label">{formatAbsenceDateRange(item)}</td>
                  <td className="px-4 py-3 tabular-nums text-label">{formatAbsenceDays(item.total_days)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={clsx(
                        "rounded-full px-2.5 py-0.5 text-xs font-semibold",
                        statusBadgeClass(item.status),
                      )}
                    >
                      {ABSENCE_STATUS_LABELS[item.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-label-secondary">{formatDateTime(item.requested_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {item.status !== "cancelled" ? (
                      <Button type="button" size="sm" variant="danger" onClick={() => openDelete(item)}>
                        Löschen
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={deleteTarget != null}
        onClose={closeDelete}
        size="sm"
        title="Abwesenheit löschen?"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={closeDelete} disabled={deleting}>
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={deleting}
              disabled={deleteReason.trim().length < 3}
              onClick={() => void confirmDelete()}
            >
              Mit Begründung löschen
            </Button>
          </>
        }
      >
        {deleteTarget ? (
          <div className="space-y-3 text-sm">
            <div className="rounded-ios border border-separator bg-card-elevated p-3">
              <div className="font-semibold text-label">
                {deleteTarget.user_display || deleteTarget.user_login}
              </div>
              <div className="text-label-secondary">
                {getAbsenceTypeLabel(deleteTarget.absence_type)} · {formatAbsenceDays(deleteTarget.total_days)}
              </div>
              <div className="text-label">{formatAbsenceDateRange(deleteTarget)}</div>
              <div className="mt-1 text-xs text-label-tertiary">
                Status: {ABSENCE_STATUS_LABELS[deleteTarget.status]}
              </div>
            </div>
            <TextareaInput
              label="Begründung (Pflicht)"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Warum wird diese Abwesenheit gelöscht?"
              rows={3}
              required
            />
            <p className="text-xs text-label-secondary">
              Der Eintrag wird als gelöscht markiert und erscheint nicht mehr im Montageplan. Die
              Begründung bleibt zur Nachvollziehbarkeit gespeichert.
            </p>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
